import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
  type HTMLAttributes,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

// ─── Context ─────────────────────────────────────────────────────────────────

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('Sidebar components must be used within a Sidebar');
  return ctx;
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /** Whether the sidebar starts collapsed */
  defaultCollapsed?: boolean;
  /** Controlled collapsed state */
  collapsed?: boolean;
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
}

function Sidebar({
  defaultCollapsed = false,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  className,
  children,
  ...props
}: SidebarProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const setCollapsed = useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalCollapsed(value);
      onCollapsedChange?.(value);
    },
    [isControlled, onCollapsedChange]
  );

  const toggle = useCallback(
    () => setCollapsed(!collapsed),
    [collapsed, setCollapsed]
  );

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, toggle }}>
      <aside
        className={cn(
          'flex h-full flex-col border-r border-slate-200 bg-white',
          'dark:border-slate-800 dark:bg-slate-950',
          'transition-all duration-300 ease-in-out',
          collapsed ? 'w-16' : 'w-64',
          className
        )}
        {...props}
      >
        {children}
      </aside>
    </SidebarContext.Provider>
  );
}

Sidebar.displayName = 'Sidebar';

// ─── SidebarHeader ───────────────────────────────────────────────────────────

export interface SidebarHeaderProps extends HTMLAttributes<HTMLDivElement> {}

function SidebarHeader({ className, children, ...props }: SidebarHeaderProps) {
  const { collapsed } = useSidebar();

  return (
    <div
      className={cn(
        'flex h-16 shrink-0 items-center border-b border-slate-100',
        'dark:border-slate-800',
        collapsed ? 'justify-center px-2' : 'px-4',
        'transition-all duration-300',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

SidebarHeader.displayName = 'SidebarHeader';

// ─── SidebarContent ──────────────────────────────────────────────────────────

export interface SidebarContentProps extends HTMLAttributes<HTMLDivElement> {}

function SidebarContent({
  className,
  children,
  ...props
}: SidebarContentProps) {
  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto overflow-x-hidden py-2',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

SidebarContent.displayName = 'SidebarContent';

// ─── SidebarSection ──────────────────────────────────────────────────────────

export interface SidebarSectionProps extends HTMLAttributes<HTMLDivElement> {
  /** Section header label */
  label?: string;
}

function SidebarSection({
  label,
  className,
  children,
  ...props
}: SidebarSectionProps) {
  const { collapsed } = useSidebar();

  return (
    <div className={cn('px-2 py-1', className)} {...props}>
      {label && !collapsed && (
        <div className="mb-1 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          {label}
        </div>
      )}
      {label && collapsed && (
        <div className="mx-auto my-1 h-px w-6 bg-slate-200 dark:bg-slate-700" />
      )}
      <nav className="flex flex-col gap-0.5">{children}</nav>
    </div>
  );
}

SidebarSection.displayName = 'SidebarSection';

// ─── SidebarItem ─────────────────────────────────────────────────────────────

export interface SidebarItemProps extends HTMLAttributes<HTMLButtonElement> {
  /** Icon displayed before the label */
  icon?: ReactNode;
  /** Whether this item is currently active */
  active?: boolean;
  /** Badge count displayed on the right */
  badge?: number | string;
  /** Whether the item is disabled */
  disabled?: boolean;
}

function SidebarItem({
  icon,
  active = false,
  badge,
  disabled = false,
  className,
  children,
  onClick,
  ...props
}: SidebarItemProps) {
  const { collapsed } = useSidebar();

  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'group flex w-full items-center gap-3 rounded-lg text-sm font-medium',
        'transition-all duration-150 outline-none',
        collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2',
        active
          ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
        'focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
      title={collapsed ? (typeof children === 'string' ? children : undefined) : undefined}
      onClick={onClick}
      {...props}
    >
      {icon && (
        <span
          className={cn(
            'shrink-0 [&>svg]:h-5 [&>svg]:w-5',
            active
              ? 'text-indigo-600 dark:text-indigo-400'
              : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300'
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
      {!collapsed && (
        <>
          <span className="flex-1 truncate text-left">{children}</span>
          {badge !== undefined && badge !== null && (
            <span
              className={cn(
                'ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                active
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
              )}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </button>
  );
}

SidebarItem.displayName = 'SidebarItem';

// ─── SidebarFooter ───────────────────────────────────────────────────────────

export interface SidebarFooterProps extends HTMLAttributes<HTMLDivElement> {}

function SidebarFooter({
  className,
  children,
  ...props
}: SidebarFooterProps) {
  const { collapsed } = useSidebar();

  return (
    <div
      className={cn(
        'shrink-0 border-t border-slate-100 dark:border-slate-800',
        collapsed ? 'px-2 py-3' : 'px-3 py-3',
        'transition-all duration-300',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

SidebarFooter.displayName = 'SidebarFooter';

// ─── SidebarToggle ───────────────────────────────────────────────────────────

export interface SidebarToggleProps
  extends HTMLAttributes<HTMLButtonElement> {}

function SidebarToggle({ className, ...props }: SidebarToggleProps) {
  const { collapsed, toggle } = useSidebar();

  return (
    <button
      type="button"
      onClick={toggle}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-md',
        'text-slate-400 transition-colors',
        'hover:bg-slate-100 hover:text-slate-600',
        'dark:hover:bg-slate-800 dark:hover:text-slate-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        className
      )}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      {...props}
    >
      {collapsed ? (
        <ChevronRight className="h-4 w-4" />
      ) : (
        <ChevronLeft className="h-4 w-4" />
      )}
    </button>
  );
}

SidebarToggle.displayName = 'SidebarToggle';

export {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarSection,
  SidebarItem,
  SidebarFooter,
  SidebarToggle,
};
